import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoleBadge from './RoleBadge';

const { FiExternalLink, FiMail, FiStar } = FiIcons;

const ProfileCard = ({ profile, showMatchScore = false }) => {
  const { createMatchRequest } = useData();
  const { user } = useAuth();

  const handleRequestIntro = () => {
    createMatchRequest(user.id, profile.id, 'Hi! I\'d like to connect and explore potential collaboration opportunities.');
    alert('Introduction request sent!');
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6"
    >
      {/* Header with Logo */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          {/* Logo/Avatar */}
          {profile.logo ? (
            <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-200 flex-shrink-0">
              <img
                src={profile.logo}
                alt={`${profile.company || profile.name} logo`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-lg">
                {(profile.name || '').charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {profile.name}
            </h3>
            {profile.company && (
              <p className="text-sm text-gray-600 mb-2 truncate">{profile.company}</p>
            )}
            <RoleBadge role={profile.role} />
          </div>
        </div>

        {showMatchScore && profile.matchScore && (
          <div className="flex items-center space-x-1 bg-yellow-50 px-2 py-1 rounded-full flex-shrink-0">
            <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-700">
              {Math.round(profile.matchScore * 100)}%
            </span>
          </div>
        )}
      </div>

      {/* Business Type */}
      {profile.businessType && (
        <p className="text-sm text-gray-600 mb-3">{profile.businessType}</p>
      )}

      {/* Platforms & Genres */}
      <div className="space-y-2 mb-4">
        {profile.platforms && profile.platforms.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Platforms</p>
            <div className="flex flex-wrap gap-1">
              {profile.platforms.slice(0, 3).map(platform => (
                <span
                  key={platform}
                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                >
                  {platform}
                </span>
              ))}
              {profile.platforms.length > 3 && (
                <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                  +{profile.platforms.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {profile.genres && profile.genres.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Genres</p>
            <div className="flex flex-wrap gap-1">
              {profile.genres.slice(0, 3).map(genre => (
                <span
                  key={genre}
                  className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full"
                >
                  {genre}
                </span>
              ))}
              {profile.genres.length > 3 && (
                <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                  +{profile.genres.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {profile.summary && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {profile.summary}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <Link
          to={`/profile/${profile.id}`}
          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center space-x-1"
        >
          <span>View Profile</span>
          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
        </Link>

        <button
          onClick={handleRequestIntro}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors inline-flex items-center space-x-1"
        >
          <SafeIcon icon={FiMail} className="w-4 h-4" />
          <span>Request Intro</span>
        </button>
      </div>
    </motion.div>
  );
};

export default ProfileCard;